/**
 * Mock da plataforma NPS (rotas /bff/nps/*). Espelha a API do fixe-app, agora
 * unificada dentro do dash-web. Dados em memória, mutáveis na sessão.
 */
import type {
  AnswerDataPoint,
  AnswerMonthlyTotals,
  AnswerRanking,
  ContentI18n,
  IntegrationEvent,
  IntegrationProvider,
  NpsAnswer,
  NpsIntegration,
  NpsQuestion,
  NpsSurvey,
  NpsUser,
  SurveyRanking,
} from '@/shared/types/nps';
import { localizeSurvey } from '@/features/nps/model/content-i18n';
import { normalizePublicSurveyLanguage } from '@/features/nps/public-survey/i18n';

interface MockResult {
  status: number;
  data: unknown;
}
const ok = (data: unknown): MockResult => ({ status: 200, data });
const created = (data: unknown): MockResult => ({ status: 201, data });
const notFound = (): MockResult => ({
  status: 404,
  data: { message: 'NPS: não encontrado (mock).' },
});

/** Strip expiration when not IDENTIFIED (aligns with api-v2 transform). */
function sanitizeSurveyBody(b: Record<string, unknown>): Record<string, unknown> {
  const responseType = (b.response_type as string) ?? 'ANONYMOUS';
  if (responseType !== 'IDENTIFIED') {
    const { response_expiration_time: _exp, ...rest } = b;
    return {
      ...rest,
      response_type: responseType,
      identification_required: false,
      answer_bonus: false,
    };
  }
  return b;
}

/** ID fixo para preview local: /surveys/preview → esta pesquisa. */
export const MOCK_PUBLIC_SURVEY_ID = 'srv_demo_public';

const DEMO_PUBLIC_SURVEY_I18N = {
  'pt-PT': {
    title: 'Inquérito de Satisfação Tasca do Marquês',
    description:
      'Responda e ajude-nos a melhorar sempre os nossos serviços<br>Obrigado pela sua participação!',
  },
  en: {
    title: 'Tasca do Marquês Satisfaction Survey',
    description:
      'Answer and help us continuously improve our services<br>Thank you for your participation!',
  },
  es: {
    title: 'Encuesta de Satisfacción Tasca do Marquês',
    description:
      'Responde y ayúdanos a mejorar siempre nuestros servicios<br>¡Gracias por tu participación!',
  },
} as const;

const DEMO_Q_I18N = {
  nome: {
    'pt-PT': { title: 'Nome', description: 'Indique o seu nome completo' },
    en: { title: 'Name', description: 'Enter your full name' },
    es: { title: 'Nombre', description: 'Indique su nombre completo' },
  },
  email: {
    'pt-PT': { title: 'E-mail', description: 'Indique o seu e-mail' },
    en: { title: 'Email', description: 'Enter your email' },
    es: { title: 'Correo', description: 'Indique su correo electrónico' },
  },
  select: {
    'pt-PT': { title: 'Como nos conheceu?' },
    en: { title: 'How did you hear about us?' },
    es: { title: '¿Cómo nos conoció?' },
  },
} as const;

const now = () => new Date().toISOString();
const daysAgo = (d: number) => {
  const x = new Date();
  x.setDate(x.getDate() - d);
  return x.toISOString();
};

const MOCK_TENANT_USER: NpsUser = {
  id: 'nu_demo_tenant',
  email: 'tasca@demo.fixe.me',
  name: 'Tasca do Marquês',
  last_name: 'Demo',
  banner: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=400&fit=crop',
  profile: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=400&fit=crop',
  roles: ['org'],
  created_at: daysAgo(400),
  user_preference: { primary_color: '#7f1d1d', secondary_color: '#b91c1c' },
};

const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

/* ──────────────────────────── seed ──────────────────────────── */

const PROVIDERS: IntegrationProvider[] = [
  {
    id: 'prov_nuvem',
    type: 'NUVEMSHOP',
    name: 'Nuvemshop',
    description: 'Plataforma de e-commerce brasileira.',
  },
  {
    id: 'prov_woo',
    type: 'WOOCOMMERCE',
    name: 'WooCommerce',
    description: 'Loja WordPress / WooCommerce.',
  },
  {
    id: 'prov_shopify',
    type: 'SHOPIFY',
    name: 'Shopify',
    description: 'Plataforma de e-commerce global.',
  },
  { id: 'prov_wbuy', type: 'WBUY', name: 'Wbuy', description: 'Plataforma de e-commerce Wbuy.' },
];

function mainQuestion(surveyId: string): NpsQuestion {
  return {
    id: `${surveyId}_q0`,
    survey_id: surveyId,
    title: 'Em uma escala de 0 a 10, o quanto você recomendaria nossa loja a um amigo?',
    description: 'Sua opinião nos ajuda a melhorar.',
    required: true,
    response_type: 'RANGE',
    response_metadata: { min: 0, max: 10 },
    position: 0,
  };
}

function buildDemoPublicQuestions(surveyId: string): NpsQuestion[] {
  return [
    {
      id: `${surveyId}_q_nome`,
      survey_id: surveyId,
      title: 'Nome',
      description: 'Informe seu Nome completo',
      i18n: { ...DEMO_Q_I18N.nome },
      required: true,
      response_type: 'TEXT',
      response_metadata: { input_type: 'input' },
      position: 0,
    },
    {
      id: `${surveyId}_q_email`,
      survey_id: surveyId,
      title: 'E-mail',
      description: 'Informe seu e-mail',
      i18n: { ...DEMO_Q_I18N.email },
      required: true,
      response_type: 'TEXT',
      response_metadata: { input_type: 'input' },
      position: 1,
    },
    {
      id: `${surveyId}_q_cel`,
      survey_id: surveyId,
      title: 'Celular',
      description: 'Informe o número do seu Celular',
      required: true,
      response_type: 'TEXT',
      response_metadata: { input_type: 'input' },
      position: 2,
    },
    {
      id: `${surveyId}_q_comida`,
      survey_id: surveyId,
      title: 'Comida',
      description: 'Como você avalia nossa Comida?',
      required: true,
      response_type: 'RANGE',
      response_metadata: { min: 0, max: 10 },
      position: 3,
    },
    {
      id: `${surveyId}_q_servico`,
      survey_id: surveyId,
      title: 'Serviço',
      description: 'Como você avalia nosso Serviço?',
      required: true,
      response_type: 'RANGE',
      response_metadata: { min: 0, max: 10 },
      position: 4,
    },
    {
      id: `${surveyId}_q_ambiente`,
      survey_id: surveyId,
      title: 'Ambiente',
      description: 'Como você avalia nosso Ambiente?',
      required: true,
      response_type: 'RANGE',
      response_metadata: { min: 0, max: 10 },
      position: 5,
    },
    {
      id: `${surveyId}_q_atend`,
      survey_id: surveyId,
      title: 'Atendimento',
      description: 'Como você avalia nosso atendimento?',
      required: false,
      response_type: 'STAR',
      response_metadata: { max: 5 },
      position: 6,
    },
    {
      id: `${surveyId}_q_bool`,
      survey_id: surveyId,
      title: 'Voltaria ao restaurante?',
      required: true,
      response_type: 'BOOLEAN',
      position: 7,
    },
    {
      id: `${surveyId}_q_select`,
      survey_id: surveyId,
      title: 'Como nos conheceu?',
      i18n: { ...DEMO_Q_I18N.select },
      required: false,
      response_type: 'SELECT',
      response_metadata: {
        options: [
          {
            label: 'Indicação',
            value: 'indicacao',
            labels: { 'pt-PT': 'Indicação', en: 'Referral', es: 'Recomendación' },
          },
          {
            label: 'Redes sociais',
            value: 'social',
            labels: { 'pt-PT': 'Redes sociais', en: 'Social media', es: 'Redes sociales' },
          },
          {
            label: 'Passando na rua',
            value: 'rua',
            labels: { 'pt-PT': 'A passar na rua', en: 'Walking by', es: 'Pasando por la calle' },
          },
        ],
      },
      position: 8,
    },
    {
      id: `${surveyId}_q_sug`,
      survey_id: surveyId,
      title: 'Sugestão',
      description: 'Existe algum comentário, elogio ou sugestão?',
      required: false,
      response_type: 'TEXT',
      response_metadata: { input_type: 'textarea' },
      position: 9,
    },
  ];
}

function buildQuestions(surveyId: string): NpsQuestion[] {
  return [
    mainQuestion(surveyId),
    {
      id: `${surveyId}_q1`,
      survey_id: surveyId,
      title: 'Como você avalia o atendimento?',
      required: false,
      response_type: 'STAR',
      response_metadata: { max: 5 },
      position: 1,
    },
    {
      id: `${surveyId}_q2`,
      survey_id: surveyId,
      title: 'O que mais pesou na sua experiência?',
      required: false,
      response_type: 'SELECT',
      response_metadata: {
        options: [
          { label: 'Preço', value: 'preco' },
          { label: 'Qualidade', value: 'qualidade' },
          { label: 'Atendimento', value: 'atendimento' },
          { label: 'Entrega', value: 'entrega' },
        ],
      },
      position: 2,
    },
    {
      id: `${surveyId}_q3`,
      survey_id: surveyId,
      title: 'Deixe um comentário (opcional)',
      required: false,
      response_type: 'TEXT',
      response_metadata: { input_type: 'textarea' },
      position: 3,
    },
  ];
}

function npsScore(scores: number[]): number {
  if (scores.length === 0) return 0;
  const prom = scores.filter((s) => s >= 9).length;
  const det = scores.filter((s) => s <= 6).length;
  return Math.round(((prom - det) / scores.length) * 100);
}

function buildAnswers(surveyId: string, count: number): NpsAnswer[] {
  const out: NpsAnswer[] = [];
  for (let i = 0; i < count; i++) {
    const seed = (surveyId.length * 31 + i * 17) % 11;
    const score = [10, 9, 9, 8, 7, 6, 10, 9, 8, 5, 9][seed];
    out.push({
      id: `${surveyId}_a${i}`,
      survey_id: surveyId,
      identifier: i % 3 === 0 ? `${10000000000 + i}` : undefined,
      created_at: daysAgo(Math.floor((i / count) * 170)),
      score,
      answers: [
        { question_id: `${surveyId}_q0`, value: score },
        { question_id: `${surveyId}_q1`, value: Math.min(5, Math.round(score / 2)) },
        {
          question_id: `${surveyId}_q2`,
          value: ['preco', 'qualidade', 'atendimento', 'entrega'][i % 4],
        },
      ],
    });
  }
  return out;
}

interface NpsDb {
  surveys: NpsSurvey[];
  questions: Record<string, NpsQuestion[]>;
  answers: Record<string, NpsAnswer[]>;
  users: NpsUser[];
  integrations: NpsIntegration[];
  events: Record<string, IntegrationEvent[]>;
}

function seedDb(): NpsDb {
  const surveysMeta: Array<[string, string, NpsSurvey['status']]> = [
    [MOCK_PUBLIC_SURVEY_ID, 'Tasca do Marquês', 'ACTIVE'],
    ['srv_csat', 'Pesquisa de Satisfação — Pós-compra', 'ACTIVE'],
    ['srv_nps_loja', 'NPS Geral da Loja', 'ACTIVE'],
    ['srv_entrega', 'Avaliação de Entrega', 'PAUSED'],
    ['srv_blackfriday', 'Black Friday 2025', 'FINISHED'],
    ['srv_rascunho', 'Nova pesquisa (rascunho)', 'DRAFT'],
  ];

  const surveys: NpsSurvey[] = [];
  const questions: NpsDb['questions'] = {};
  const answers: NpsDb['answers'] = {};

  surveysMeta.forEach(([id, title, status], i) => {
    questions[id] =
      id === MOCK_PUBLIC_SURVEY_ID ? buildDemoPublicQuestions(id) : buildQuestions(id);
    const defaultCounts = [42, 180, 240, 64, 320];
    const count = status === 'DRAFT' ? 0 : (defaultCounts[i] ?? 0);
    answers[id] = buildAnswers(id, count);
    const score = npsScore(answers[id].map((a) => a.score ?? 0));
    surveys.push({
      id,
      title,
      description:
        id === MOCK_PUBLIC_SURVEY_ID
          ? 'Responda e nos ajude a sempre melhorar nossos serviços<br>Obrigado pela sua participação!'
          : 'Pesquisa para medir a experiência dos clientes.',
      ...(id === MOCK_PUBLIC_SURVEY_ID ? { i18n: { ...DEMO_PUBLIC_SURVEY_I18N } } : {}),
      status,
      response_type: id === MOCK_PUBLIC_SURVEY_ID || i % 2 === 0 ? 'IDENTIFIED' : 'ANONYMOUS',
      response_expiration_time: 'THREE_MONTHS',
      identification_required: id === MOCK_PUBLIC_SURVEY_ID ? true : i % 2 === 0,
      answer_bonus: id === MOCK_PUBLIC_SURVEY_ID ? true : false,
      created_at: daysAgo(200 - i * 20),
      updated_at: daysAgo(i * 3),
      finished_at: status === 'FINISHED' ? daysAgo(10) : null,
      answers_count: count,
      nps_score: score,
    });
  });

  const users: NpsUser[] = [
    {
      id: 'nu_admin',
      email: 'nps@fixe.me',
      name: 'NPS',
      last_name: 'Admin',
      roles: ['admin', 'org'],
      created_at: daysAgo(300),
      user_preference: { primary_color: '#2563eb', secondary_color: '#7c3aed' },
    },
    {
      id: 'nu_ana',
      email: 'ana@fixe.me',
      name: 'Ana',
      last_name: 'Souza',
      roles: ['org'],
      created_at: daysAgo(120),
    },
    {
      id: 'nu_bruno',
      email: 'bruno@fixe.me',
      name: 'Bruno',
      last_name: 'Lima',
      roles: ['org'],
      created_at: daysAgo(60),
    },
  ];

  const integrations: NpsIntegration[] = [
    {
      id: 'int_nuvem',
      provider: PROVIDERS[0],
      store_url: 'minhaloja.lojavirtualnuvem.com.br',
      created_at: daysAgo(90),
      integration_webhooks: [
        { id: 'wh_1', integration_id: 'int_nuvem', type: 'ORDER_PAID', created_at: daysAgo(90) },
      ],
    },
  ];

  const events: NpsDb['events'] = {
    int_nuvem: Array.from({ length: 12 }, (_, k) => ({
      id: `ev_${k}`,
      event_id: `order_${1000 + k}`,
      integration_id: 'int_nuvem',
      type: 'ORDER_PAID',
      status: k % 7 !== 0,
      created_at: daysAgo(k * 2),
      body: { total: 150 + k * 23.5, currency: 'BRL' },
    })),
  };

  return { surveys, questions, answers, users, integrations, events };
}

const db = seedDb();

/* ──────────────────────────── helpers ──────────────────────────── */

function withQuestions(s: NpsSurvey): NpsSurvey {
  const base = {
    ...s,
    questions: [...(db.questions[s.id] ?? [])].sort((a, b) => a.position - b.position),
  };
  if (s.id === MOCK_PUBLIC_SURVEY_ID) {
    return { ...base, user: { ...MOCK_TENANT_USER } };
  }
  return base;
}

function answerSeries(): AnswerDataPoint[] {
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  const m = new Date().getMonth();
  const out: AnswerDataPoint[] = [];
  for (let i = 5; i >= 0; i--) {
    const idx = (m - i + 12) % 12;
    const promoters = 40 + ((idx * 7) % 30);
    const passives = 15 + ((idx * 3) % 12);
    const detractors = 5 + ((idx * 2) % 10);
    out.push({
      month: months[idx],
      promoters,
      passives,
      detractors,
      total: promoters + passives + detractors,
    });
  }
  return out;
}

function answerMonthlyTotals(): AnswerMonthlyTotals {
  const months = answerSeries();
  const now = new Date();
  const out: AnswerMonthlyTotals = {};
  months.forEach((m, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    out[key] = m.total;
  });
  return out;
}

function isMockNpsMaster(email?: string | null): boolean {
  return email?.trim().toLowerCase() === 'admin@fixecrm.com';
}

function ranking(): SurveyRanking[] {
  const me = db.users[0];
  const isAdmin = isMockNpsMaster(me?.email);
  return db.surveys
    .filter((s) => s.status !== 'DRAFT')
    .filter((s) => isAdmin || !s.user_id || s.user_id === me?.id)
    .map((s) => ({
      id: s.id,
      title: s.title,
      status: s.status,
      answers_count: s.answers_count ?? 0,
      nps_score: s.nps_score ?? 0,
    }))
    .sort((a, b) => b.nps_score - a.nps_score);
}

function orgRanking(): AnswerRanking[] {
  return db.users
    .filter((u) => u.roles?.includes('org'))
    .map((u) => ({
      id: u.id,
      title: `${u.name} ${u.last_name ?? ''}`.trim(),
      description: u.email,
      status: 'ACTIVE' as const,
      response_type: 'IDENTIFIED' as const,
      response_expiration_time: 'NEVER' as const,
      created_at: u.created_at ?? now(),
      updated_at: u.created_at ?? now(),
      total_answers: db.surveys.reduce((acc, s) => acc + (s.answers_count ?? 0), 0),
      user: u,
    }))
    .sort((a, b) => b.total_answers - a.total_answers);
}

function recomputeSurveyStats(surveyId: string) {
  const s = db.surveys.find((x) => x.id === surveyId);
  if (!s) return;
  const a = db.answers[surveyId] ?? [];
  s.answers_count = a.length;
  s.nps_score = npsScore(a.map((x) => x.score ?? 0));
}

/* ──────────────────────────── dispatcher ──────────────────────────── */

export function handleNps(
  method: string,
  seg: string[],
  body: unknown,
  query?: URLSearchParams,
): MockResult {
  const b = (body ?? {}) as Record<string, unknown>;
  const lang = normalizePublicSurveyLanguage(query?.get('lang'));

  /* ---- SURVEYS ---- */
  if (seg[0] === 'surveys' && seg.length === 1) {
    if (method === 'GET') {
      const me = db.users[0];
      const isAdmin = isMockNpsMaster(me?.email);
      const list = isAdmin
        ? db.surveys
        : db.surveys.filter((s) => !s.user_id || s.user_id === me?.id);
      return ok(list.map((s) => ({ ...s })));
    }    if (method === 'POST') {
      const sanitized = sanitizeSurveyBody(b);
      const responseType = (sanitized.response_type as NpsSurvey['response_type']) ?? 'ANONYMOUS';
      const id = uid('srv');
      const survey: NpsSurvey = {
        id,
        title: String(sanitized.title ?? 'Nova pesquisa'),
        description: (sanitized.description as string) ?? '',
        i18n: (sanitized.i18n as ContentI18n | undefined) ?? undefined,
        status: 'DRAFT',
        response_type: responseType,
        response_expiration_time:
          responseType === 'IDENTIFIED'
            ? ((sanitized.response_expiration_time as NpsSurvey['response_expiration_time']) ??
              'NEVER')
            : ('NEVER' as NpsSurvey['response_expiration_time']),
        identification_required:
          responseType === 'IDENTIFIED' ? Boolean(sanitized.identification_required) : false,
        answer_bonus: responseType === 'IDENTIFIED' ? Boolean(sanitized.answer_bonus) : false,
        created_at: now(),
        updated_at: now(),
        answers_count: 0,
        nps_score: 0,
        user_id: sanitized.user_id as string | undefined,
      };
      db.surveys.unshift(survey);
      db.questions[id] = [mainQuestion(id)];
      db.answers[id] = [];
      return created(survey);
    }
  }
  if (seg[0] === 'surveys' && seg[1]) {
    const survey = db.surveys.find((s) => s.id === seg[1]);
    if (!survey) return notFound();
    if (method === 'GET') {
      const full = withQuestions(survey);
      // Admin detail (sem lang / pt-BR) devolve i18n completo; público projeta.
      if (lang !== 'pt-BR') return ok(localizeSurvey(full, lang));
      return ok(full);
    }
    if (method === 'PUT') {
      survey.status = (b.status as NpsSurvey['status']) ?? survey.status;
      survey.updated_at = now();
      if (survey.status === 'FINISHED') survey.finished_at = now();
      return ok(survey);
    }
    if (method === 'PATCH') {
      const nextType = (b.response_type as NpsSurvey['response_type']) ?? survey.response_type;
      const sanitized = sanitizeSurveyBody({
        ...b,
        response_type: nextType,
      });
      Object.assign(survey, {
        title: sanitized.title ?? survey.title,
        description: sanitized.description ?? survey.description,
        i18n: sanitized.i18n !== undefined ? sanitized.i18n : survey.i18n,
        response_type: nextType,
        response_expiration_time:
          nextType === 'IDENTIFIED'
            ? ((sanitized.response_expiration_time as NpsSurvey['response_expiration_time']) ??
              survey.response_expiration_time)
            : ('NEVER' as NpsSurvey['response_expiration_time']),
        identification_required:
          nextType === 'IDENTIFIED'
            ? (sanitized.identification_required ?? survey.identification_required)
            : false,
        answer_bonus:
          nextType === 'IDENTIFIED' ? (sanitized.answer_bonus ?? survey.answer_bonus) : false,
        updated_at: now(),
      });
      return ok(survey);
    }
  }

  /* ---- QUESTIONS ---- */
  if (
    seg[0] === 'questions' &&
    seg[1] === 'survey' &&
    seg[3] === 'positions' &&
    method === 'PATCH'
  ) {
    const surveyId = seg[2];
    const items = (b.items as Array<{ id: string; position: number }>) ?? [];
    const list = db.questions[surveyId] ?? [];
    for (const it of items) {
      const q = list.find((x) => x.id === it.id);
      if (q) q.position = it.position;
    }
    return ok([...list].sort((a, b2) => a.position - b2.position));
  }
  if (seg[0] === 'questions' && seg[1] && method === 'POST') {
    const surveyId = seg[1];
    const list = (db.questions[surveyId] ??= []);
    const q: NpsQuestion = {
      id: uid('q'),
      survey_id: surveyId,
      title: String(b.title ?? 'Nova pergunta'),
      description: b.description as string,
      i18n: (b.i18n as ContentI18n | undefined) ?? undefined,
      required: Boolean(b.required),
      response_type: (b.response_type as NpsQuestion['response_type']) ?? 'TEXT',
      response_metadata: (b.response_metadata as NpsQuestion['response_metadata']) ?? {},
      position: list.length,
    };
    list.push(q);
    return created(q);
  }
  if (seg[0] === 'questions' && seg[1] && method === 'PATCH') {
    for (const list of Object.values(db.questions)) {
      const q = list.find((x) => x.id === seg[1]);
      if (q) {
        Object.assign(q, {
          title: b.title ?? q.title,
          description: b.description ?? q.description,
          i18n: b.i18n !== undefined ? b.i18n : q.i18n,
          required: b.required ?? q.required,
          response_type: b.response_type ?? q.response_type,
          response_metadata: b.response_metadata ?? q.response_metadata,
        });
        return ok(q);
      }
    }
    return notFound();
  }
  if (seg[0] === 'questions' && seg[1] && method === 'DELETE') {
    for (const [sid, list] of Object.entries(db.questions)) {
      const idx = list.findIndex((x) => x.id === seg[1]);
      if (idx >= 0) {
        list.splice(idx, 1);
        list.forEach((q, i) => (q.position = i));
        db.questions[sid] = list;
        return ok({ success: true });
      }
    }
    return notFound();
  }

  /* ---- ANSWERS ---- */
  if (seg[0] === 'answers' && seg[1] === 'data' && seg[2] === 'enriched') {
    // survey_ids só filtra escopo; série mock é agregada (sem breakdown por survey).
    return ok(answerSeries());
  }
  if (seg[0] === 'answers' && seg[1] === 'data') {
    const ids = query?.get('survey_ids');
    if (ids != null && ids.split(',').filter(Boolean).length === 0) {
      return ok({});
    }
    return ok(answerMonthlyTotals());
  }
  if (seg[0] === 'answers' && seg[1] === 'ranking' && seg[2] === 'surveys') return ok(ranking());
  if (seg[0] === 'answers' && seg[1] === 'ranking') return ok(orgRanking());
  if (seg[0] === 'answers' && seg[1] && method === 'POST') {
    const surveyId = seg[1];
    const list = (db.answers[surveyId] ??= []);
    const items = (b.answers as Array<{ question_id: string; value: unknown }>) ?? [];
    const main = items.find(
      (x) => x.question_id === `${surveyId}_q0` || x.question_id.endsWith('_q0'),
    );
    const score = typeof main?.value === 'number' ? main.value : Number(main?.value ?? 0);
    list.push({
      id: uid('a'),
      survey_id: surveyId,
      identifier: b.identifier as string,
      created_at: now(),
      score,
      answers: items as NpsAnswer['answers'],
    });
    recomputeSurveyStats(surveyId);
    return created({ success: true });
  }

  /* ---- USERS ---- */
  if (seg[0] === 'users' && seg[1] === 'me') {
    const me = db.users[0];
    if (method === 'PATCH') {
      Object.assign(me, {
        name: b.name ?? me.name,
        last_name: b.last_name ?? me.last_name,
        tax_identifier: b.tax_identifier ?? me.tax_identifier,
        legacy_user_id:
          b.legacy_user_id !== undefined
            ? (b.legacy_user_id as string) || undefined
            : me.legacy_user_id,
      });
      return ok(me);
    }
    if (seg[2] === 'profile' || seg[2] === 'banner') {
      if (method === 'GET') return ok(new Blob());
      return ok(me);
    }
    return ok(me);
  }
  if (seg[0] === 'user-preferences' && method === 'PATCH') {
    const me = db.users[0];
    me.user_preference = {
      primary_color: String(b.primary_color ?? me.user_preference?.primary_color ?? '#2563eb'),
      secondary_color: String(
        b.secondary_color ?? me.user_preference?.secondary_color ?? '#7c3aed',
      ),
      logo: me.user_preference?.logo,
    };
    return ok(me.user_preference);
  }
  if (seg[0] === 'users' && seg.length === 1) {
    if (method === 'GET') return ok(db.users);
    if (method === 'POST') {
      const u: NpsUser = {
        id: uid('nu'),
        email: String(b.email ?? ''),
        name: String(b.name ?? ''),
        last_name: b.last_name as string,
        tax_identifier: b.tax_identifier as string,
        legacy_user_id: b.legacy_user_id as string,
        roles: [(b.role as NpsUser['roles'] extends (infer R)[] | undefined ? R : never) ?? 'org'],
        created_at: now(),
      };
      db.users.push(u);
      return created(u);
    }
  }
  if (seg[0] === 'users' && seg[1]) {
    const idx = db.users.findIndex((u) => u.id === seg[1]);
    if (idx < 0) return notFound();
    if (method === 'GET') return ok(db.users[idx]);
    if (method === 'PATCH') {
      Object.assign(db.users[idx], {
        name: b.name ?? db.users[idx].name,
        last_name: b.last_name ?? db.users[idx].last_name,
        tax_identifier: b.tax_identifier ?? db.users[idx].tax_identifier,
        legacy_user_id: b.legacy_user_id ?? db.users[idx].legacy_user_id,
        roles: b.role ? [b.role as 'admin' | 'org' | 'user'] : db.users[idx].roles,
      });
      return ok(db.users[idx]);
    }
    if (method === 'DELETE') {
      if (db.users[idx].id === db.users[0].id) {
        return { status: 400, data: { message: 'Não é possível remover o usuário logado.' } };
      }
      db.users.splice(idx, 1);
      return ok({ success: true });
    }
  }

  /* ---- INTEGRATIONS ---- */
  if (seg[0] === 'integrations' && seg[1] === 'shopify' && seg[2] === 'auth' && method === 'POST') {
    const store = String(b.store_name ?? 'demo');
    return ok({ url: `https://${store}.myshopify.com/admin/oauth/authorize?mock=1` });
  }
  if (seg[0] === 'integrations' && seg[1] === 'available') return ok(PROVIDERS);
  if (seg[0] === 'integrations' && seg[1] === 'webhooks' && method === 'POST') {
    const integ = db.integrations.find((x) => x.id === b.integration_id);
    if (!integ) return notFound();
    const wh = {
      id: uid('wh'),
      integration_id: String(b.integration_id),
      type: b.type as 'ORDER_PAID' | 'UNINSTALLED',
      created_at: now(),
    };
    if (!integ.integration_webhooks) integ.integration_webhooks = [];
    integ.integration_webhooks.push(wh);
    return created(wh);
  }
  if (seg[0] === 'integrations' && seg[1] === 'webhooks' && method === 'DELETE') {
    const integ = db.integrations.find((x) => x.id === seg[2]);
    if (integ)
      integ.integration_webhooks = (integ.integration_webhooks ?? []).filter(
        (w) => w.id !== seg[3],
      );
    return ok({ success: true });
  }
  if (seg[0] === 'integrations' && seg.length === 1) {
    if (method === 'GET') return ok(db.integrations);
    if (method === 'POST') {
      const provider = PROVIDERS.find((p) => p.type === b.type) ?? PROVIDERS[0];
      const integ: NpsIntegration = {
        id: uid('int'),
        provider,
        store_url: (b.params as { store_url?: string })?.store_url,
        created_at: now(),
        integration_webhooks: [],
      };
      db.integrations.push(integ);
      return created(integ);
    }
  }
  if (seg[0] === 'integrations' && seg[1] && method === 'GET') {
    const integ = db.integrations.find((x) => x.id === seg[1]);
    return integ ? ok(integ) : notFound();
  }
  if (seg[0] === 'integration-webhook-logs' && seg[1]) {
    return ok(db.events[seg[1]] ?? []);
  }

  return notFound();
}
